<template>
  <section class="t-flex-col t-rounded-md t-shadow-lg t-p-4 t-bg-white">
    <h5 class="t-font-semibold">
      Tracking
    </h5>

    <div class="t-mt-4 t-relative">
      <!-- Decide what map to show based on whether a user selects to track a booking -->
      <div
        v-if="map && !booking && !mapData"
        id="map"
        style="width: 100%; height: 48rem;"
      ></div>

      <directions
        v-else-if="booking && !directionsIsLoading && mapData"
        :origin="mapData.pickup"
        :destination="mapData.delivery"
        :latestPoint="mapData.latestPoint"
        :movements="mapMovements"
        :key="500"
        :height="48"
        :height-units="'rem'"
        :in-transit="!(booking.status_id === 5 || booking.status_id === 9)"
      />

      <!-- Tracker Dropdown Options -->
      <div class="t-absolute t-mt-2 t-ml-2 t-top-0 t-left-0">
        <div
          v-click-outside="
            () => {
              showTrackerDropdownOptions = false;
            }
          "
          class="t-mr-2 t-relative"
        >
          <button
            @click="onTrackerTypesDropdownClick"
            type="button"
            class="t-inline-flex t-justify-center t-w-full t-border t-border-gray-300 t-shadow-sm t-rounded
            t-py-1 t-px-2 t-bg-white t-text-sm t-font-medium t-text-gray-700 hover:t-bg-gray-50 focus:t-outline-none"
            id="options-menu"
            aria-haspopup="true"
            aria-expanded="true"
          >
            {{ selectedTrackerTypeDisplay }}
            <svg
              class="t--mr-1 t-ml-2 t-h-5 t-w-5"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 20 20"
              fill="currentColor"
              aria-hidden="true"
            >
              <path
                fill-rule="evenodd"
                d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                clip-rule="evenodd"
              />
            </svg>
          </button>

          <div
            v-if="showTrackerDropdownOptions"
            class="t-origin-top-right t-absolute t-mt-1 t-left-0 t-rounded-md t-shadow-lg t-bg-white t-w-24"
          >
            <div
              class="t-py-1"
              role="menu"
              aria-orientation="vertical"
              aria-labelledby="options-menu"
            >
              <div
                class="t-flex t-justify-between t-px-2 t-py-2 t-items-center"
              >
                <button
                  @click="setSelectedTrackerType(TRACKER_TYPES.ALL)"
                  class="t-mb-0 t-inline-flex t-block t-text-sm t-text-gray-700 t-hover:bg-gray-100 t-hover:text-gray-900"
                  role="menuitem"
                >
                  All Trackers
                </button>
              </div>

              <div
                class="t-flex t-justify-between t-px-2 t-py-2 t-items-center"
              >
                <button
                  @click="setSelectedTrackerType(TRACKER_TYPES.ASSETS)"
                  class="t-mb-0 t-inline-flex t-items-baseline t-space-x-2 t-block t-text-sm t-text-gray-700 t-hover:bg-gray-100 t-hover:text-gray-900"
                  role="menuitem"
                >
                  <i class="fas fa-circle t-text-blue-500" />
                  <span>Assets</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Selected Booking Map Summary -->
      <article
        v-if="booking && !directionsIsLoading && mapData"
        class="t-absolute t-mx-4 t-mb-6 t-py-2 t-px-3 t-bottom-0 t-left-0 t-right-0 t-bg-white t-border t-border-gray-300 t-rounded t-text-sm"
      >
        <div
          class="t-flex t-justify-between t-border-b-2 t-border-gray-300 t-pb-2"
        >
          <p class="t-text-lg">
            {{ booking.id }}
          </p>

          <button @click="onTrackingSummaryClose">
            <svg
              class="t-w-6 t-h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M6 18L18 6M6 6l12 12"
              ></path>
            </svg>
          </button>
        </div>

        <div class="t-grid t-grid-rows-1 t-grid-cols-12 t-mt-2">
          <div class="t-flex-col t-col-span-9 t-mr-1">
            <p class="t-text-xs t-font-semibold t-uppercase t-text-gray-600">
              Most Recent Update
            </p>
            <p v-if="latestBookingMovement" class="t-text-sm">
              {{ latestBookingMovement.content }}
            </p>

            <div class="t-flex t-flex-row t-flex-inline t-mt-1">
              <p v-if="latestBookingMovement" class="t-font-light">
                {{ latestBookingMovement.time }}
              </p>
            </div>
          </div>

          <div class="t-flex-col t-col-span-3">
            <p class="t-text-xs t-font-semibold t-uppercase t-text-gray-600">
              Due
            </p>
            <p class="t-font-light">
              {{ booking.deliverBy.format("ddd DD MMM") }}
            </p>
          </div>
        </div>
      </article>

      <article
        v-else-if="selectedAsset"
        class="t-absolute t-mx-4 t-mb-6 t-py-2 t-px-3 t-bottom-0 t-left-0 t-right-0 t-bg-white t-border t-border-gray-300 t-rounded t-text-sm"
      >
        <div
          class="t-py-1"
          role="menu"
          aria-orientation="vertical"
          aria-labelledby="options-menu"
        >
          <div
            class="t-flex t-justify-between t-border-b-2 t-border-gray-300 t-pb-2"
          >
            <p class="t-text-lg">
              {{ selectedAsset.assetName }}
            </p>

            <button @click="onAssetSummaryClose">
              <svg
                class="t-w-6 t-h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M6 18L18 6M6 6l12 12"
                ></path>
              </svg>
            </button>
          </div>

          <div class="t-mt-2">
            <p class="t-text-xs t-font-semibold t-uppercase t-text-gray-600">
              Current Location
            </p>
            {{ selectedAsset.currentLocation }}
          </div>

          <div class="t-mt-2">
            <p class="t-text-xs t-font-semibold t-uppercase t-text-gray-600">
              Last Detected
            </p>
            {{
              selectedAsset.updatedAt &&
                moment(selectedAsset.updatedAt).format("ddd MMM D YYYY hh:mm a")
            }}
          </div>
        </div>
      </article>

      <!-- An overlay that appears if they don't have any trackers attached to both current bookings or assets -->
      <v-overlay v-if="false">
        <slot name="content">
          <div class="t-flex-col">
            <div class="t-text-center t-mt-10">
              <h5 class="t-font-bold t-tracking-wide">
                Track Vehicles and Assets
              </h5>
              <p class="t-px-4">
                Gain a detailed view of where your items are and manage them
                better.
              </p>
              <button
                class="t-bg-blue-400 t-px-4 t-py-2 t-text-white t-font-semibold t-text-sm"
              >
                Find Out More
              </button>
            </div>
          </div>
        </slot>
      </v-overlay>
    </div>
  </section>
</template>

<script>
import VOverlay from "../../../utils/VOverlay";
import Directions from "../../../maps/Directions";
import moment from "moment";
import { mapMovementsToTimelineItems } from "../../../../utility/movements";
import { getMapMovementsData } from "../../../../modules/bookings";
import axios from "axios";

export default {
  name: "TrackingSection",
  components: {
    VOverlay,
    Directions
  },
  props: {
    selectedBooking: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      booking: null,
      freightTrackers: [],
      mapData: null,
      mapMovements: null,
      formattedSelectedBooking: null,
      selectedTrackerType: "ALL",
      selectedTrackerTypeDisplay: "All Trackers",
      showTrackerDropdownOptions: false,
      latestBookingMovement: null,
      directionsIsLoading: false,
      selectedAsset: null,
      moment,
      map: {
        initialZoom: 6,
        initialCenter: {
          lat: -41.0,
          lng: 174.25
        },
        zoom: 6,
        center: {
          lat: -41.0,
          lng: 174.25
        }
      },
      googleMap: null,
      markers: []
    };
  },
  created() {
    this.TRACKER_TYPES = {
      ALL: {
        type: "ALL",
        display: "All Trackers"
      },
      FREIGHT: {
        type: "FREIGHT",
        display: "Freight"
      },
      ASSETS: {
        type: "ASSETS",
        display: "Assets"
      }
    };
    this.setSelectedTrackerType(this.TRACKER_TYPES.ALL);
  },
  mounted() {
    //init google maps
    this.$initGoogleMap('map');

    this.setMarkers();
  },
  methods: {
    setMarkers() {
      this.clearMarkers();

      if (
        this.selectedTrackerType === this.TRACKER_TYPES.ALL.type ||
        this.selectedTrackerType === this.TRACKER_TYPES.ASSETS.type
      ) {
        this.assetTrackers.forEach((asset) => {
          const marker = new google.maps.Marker({
            position: { lat: asset.lat, lng: asset.lng },
            map: this.googleMap,
            icon: {
              url: `data:image/svg+xml;utf-8, \
                <svg fill='none' stroke='currentColor' width='32' height='32' viewBox='0 0 24 24' xmlns='http://www.w3.org/2000/svg'> \
                  <path fill='%2360A5FA' stroke-width='1' d='M17.657,16.657L13.414,20.9a1.998,1.998,0,01-2.827,0l-4.244-4.243a8,8,0,1111.314,0z'></path> \
                  <path fill='%23000000' stroke-width='1' d='M15 11a3 3 0 11-6 0 3 3 0 016 0z'></path> \
                </svg>`,
            },
          });

          marker.addListener("click", () => {
            this.onMapAssetClick(asset);
          });

          this.markers.push(marker);
        });
      }
    },
    clearMarkers() {
      this.markers.forEach(marker => marker.setMap(null));
      this.markers = [];
    },
    onTrackerTypesDropdownClick() {
      this.showTrackerDropdownOptions = !this.showTrackerDropdownOptions;
    },
    setSelectedTrackerType(trackerType) {
      this.selectedTrackerType = trackerType.type;
      this.selectedTrackerTypeDisplay = trackerType.display;

      // Close the dropdown options once a tracker type is selected
      this.showTrackerDropdownOptions = false;

      // Update markers
      if (this.googleMap) {
        this.setMarkers();
      }
    },
    async onMapFreightTrackerClick(booking) {
      await this.onNewSelectedBooking(booking);
    },
    onMapAssetClick(asset) {
      this.onTrackingSummaryClose();
      this.onAssetSummaryClose();

      this.selectedAsset = asset;
      this.map = {
        ...this.map,
        zoom: 15,
        center: {
          lat: asset.lat,
          lng: asset.lng
        }
      };
      this.googleMap.setZoom(this.map.zoom);
      this.googleMap.setCenter(this.map.center);
    },
    async onNewSelectedBooking(newSelectedBooking) {
      this.onTrackingSummaryClose();
      this.onAssetSummaryClose();
      this.mapData = null;
      this.directionsIsLoading = true;
      this.booking = newSelectedBooking;

      if (newSelectedBooking.tracker) {
        this.map.center = {
          lat: newSelectedBooking.tracker.last_location.coordinates[1],
          lng: newSelectedBooking.tracker.last_location.coordinates[0]
        };
        this.googleMap.setCenter(this.map.center);
      }

      try {
        this.latestBookingMovement = null;
        let res = await axios.get(
          `/send-freight/bookings/${this.booking.id}?t=` + Date.now()
        );
        let booking = res.data.data;

        let timelineItems = mapMovementsToTimelineItems(
          booking.movements.data,
          booking
        );
        this.latestBookingMovement = timelineItems[timelineItems.length - 1];

        this.mapMovements = booking.movements;

        this.mapData = await getMapMovementsData(this.booking.id);
      } catch (error) {
        console.error(error);
      } finally {
        this.directionsIsLoading = false;
      }
    },
    onTrackingSummaryClose() {
      this.booking = null;
      this.mapData = null;
      this.mapMovements = null;
    },
    onAssetSummaryClose() {
      this.selectedAsset = null;
    }
  },
  computed: {
    freightBookingTrackers() {
      let bookings = this.$store.getters.getBookings;
      let bookingTrackers = [];
      bookings.forEach(booking => {
        if (booking.tracker) {
          bookingTrackers.push(booking);
        }
      });
      return bookingTrackers;
    },
    assetTrackers() {
      let assets = this.$store.getters.getAssets;
      return assets ? assets : [];
    }
  },
  watch: {
    /**
     *
     * @returns {Promise<void>}
     */
    selectedBooking: async function(newSelectedBooking) {
      await this.onNewSelectedBooking(newSelectedBooking);
    }
  }
};
</script>
